import { z } from 'zod';
import { COSMETIC_OFFERS } from './cosmetics/index.js';
import { getItem } from './items.js';
import { inSaleWindow, parisMonthDay } from '../rules/sale-window.js';
/**
 * The shop (ADR 0031): cosmetics sold for real money, each offer paid on its
 * own through Stripe Checkout and delivered as items to the character that
 * bought it. Prices are in cents of one currency, fixed here like every other
 * game datum; the server never trusts a price from the client.
 */
export const SHOP_CURRENCY = 'eur';
export const SHOP_PACKS = [
    'founder',
    'dragon',
    'scarecrow',
    'aurora',
    'dragonslayer',
    'sylvan',
    'bogwitch',
    'djinn',
    'shaman',
];
/**
 * Packs sold only part of the year (`rules/sale-window.ts`): every offer of
 * the pack, bundle and singles, is out of the shop outside its window. What
 * was bought stays delivered and worn all year.
 */
export const SHOP_PACK_SEASONS = {
    // Halloween, and the night of the Great Scarecrow.
    scarecrow: { from: '10-15', until: '11-05' },
};
/** Whether an offer can be bought at `now`. */
export function isOnSale(offer, now) {
    const window = offer.pack === undefined ? undefined : SHOP_PACK_SEASONS[offer.pack];
    return window === undefined || inSaleWindow(window, parisMonthDay(now));
}
const OFFER_ID = /^[a-z][a-z0-9-]*$/;
export const ShopOfferSchema = z
    .object({
    id: z.string().regex(OFFER_ID, 'kebab-case id'),
    /** Player-facing, French. */
    name: z.string().min(1).max(40),
    /** One or two sentences under the name, French. */
    blurb: z.string().min(1).max(200),
    /**
     * The pack an offer belongs to: a bundle always has one, and its pieces
     * sold singly carry the same, so the shop shows them on the pack's page
     * rather than on its home. A single without a pack stands on the home.
     */
    pack: z.enum(SHOP_PACKS).optional(),
    /** What the buyer receives, item by item; a bundle lists several. */
    items: z
        .array(z.object({
        templateId: z.string().min(1),
        quantity: z.number().int().min(1).max(99).default(1),
    }))
        .min(1),
    /** In cents of `SHOP_CURRENCY`; Stripe wants whole minor units. */
    priceCents: z.number().int().min(50),
    /** Shown struck through beside the price when a bundle undercuts its parts. */
    worthCents: z.number().int().min(50).optional(),
})
    .superRefine((offer, ctx) => {
    for (const [index, item] of offer.items.entries()) {
        const template = getItem(item.templateId);
        if (template === undefined) {
            ctx.addIssue({
                code: 'custom',
                path: ['items', index, 'templateId'],
                message: `Unknown item ${item.templateId}`,
            });
            continue;
        }
        // What is sold is a look: a cosmetic over the real gear, or a companion.
        const isLook = template.cosmetic || template.companion !== undefined;
        if (!isLook) {
            ctx.addIssue({
                code: 'custom',
                path: ['items', index, 'templateId'],
                message: `${item.templateId} gives stats: the shop sells looks only`,
            });
        }
        if (template.lootable) {
            ctx.addIssue({
                code: 'custom',
                path: ['items', index, 'templateId'],
                message: `${item.templateId} is found in the world and cannot be sold for money`,
            });
        }
    }
    if (offer.items.length > 1 && offer.pack === undefined) {
        ctx.addIssue({ code: 'custom', path: ['pack'], message: 'A bundle names its pack' });
    }
    if (offer.worthCents !== undefined && offer.worthCents <= offer.priceCents) {
        ctx.addIssue({ code: 'custom', path: ['worthCents'], message: 'A worth is above the price' });
    }
});
const DEFINITIONS = [
    {
        id: 'flame-sword',
        name: 'Épée de flamme',
        blurb: 'Une lame d’obsidienne dévorée par les flammes, portée par-dessus ton arme. Animée.',
        pack: 'founder',
        items: [{ templateId: 'flame-sword' }],
        priceCents: 499,
    },
    {
        id: 'fire-hound',
        name: 'Chien de feu',
        blurb: 'Un chien de braise fendu de lave qui trotte à ta droite, la queue en feu. Animé.',
        pack: 'founder',
        items: [{ templateId: 'fire-hound' }],
        priceCents: 399,
    },
    {
        id: 'fire-horse',
        name: 'Cheval de feu',
        blurb: 'Une monture de lave et de flammes, deux fois plus rapide que tes jambes. Animée.',
        pack: 'founder',
        items: [{ templateId: 'fire-horse' }],
        priceCents: 699,
    },
    {
        id: 'flame-helm',
        name: 'Heaume de flamme',
        blurb: 'Obsidienne fendue de lave, deux cornes, une crête de flammes. Animé.',
        pack: 'founder',
        items: [{ templateId: 'flame-helm' }],
        priceCents: 399,
    },
    {
        id: 'flame-chest',
        name: 'Cuirasse de flamme',
        blurb: 'Plaques de pierre volcanique, braise au cœur, flammes aux épaules. Animée.',
        pack: 'founder',
        items: [{ templateId: 'flame-chest' }],
        priceCents: 499,
    },
    {
        id: 'flame-gloves',
        name: 'Gantelets de flamme',
        blurb: 'Des poings d’obsidienne aux jointures de braise. Animés.',
        pack: 'founder',
        items: [{ templateId: 'flame-gloves' }],
        priceCents: 299,
    },
    {
        id: 'flame-boots',
        name: 'Bottes de flamme',
        blurb: 'Chaque pas laisse monter une flamme des chevilles. Animées.',
        pack: 'founder',
        items: [{ templateId: 'flame-boots' }],
        priceCents: 299,
    },
    {
        id: 'flame-shield',
        name: 'Bouclier de flamme',
        blurb: 'Un disque d’obsidienne cerclé de feu, la braise au centre. Animé.',
        pack: 'founder',
        items: [{ templateId: 'flame-shield' }],
        priceCents: 399,
    },
    {
        id: 'flame-wings',
        name: 'Ailes de flamme',
        blurb: 'Des ailes d’obsidienne fendues de lave, les pointes en feu, dans ton dos. Animées.',
        pack: 'founder',
        items: [{ templateId: 'flame-wings' }],
        priceCents: 799,
    },
    {
        id: 'founder-pack',
        name: 'Pack fondateur',
        blurb: 'La panoplie complète de ceux qui étaient là au début : les six pièces de flamme, les ailes de flamme, le chien de feu à tes côtés et le cheval de feu sous ta selle.',
        pack: 'founder',
        items: [
            { templateId: 'flame-sword' },
            { templateId: 'flame-helm' },
            { templateId: 'flame-chest' },
            { templateId: 'flame-gloves' },
            { templateId: 'flame-boots' },
            { templateId: 'flame-shield' },
            { templateId: 'flame-wings' },
            { templateId: 'fire-hound' },
            { templateId: 'fire-horse' },
        ],
        priceCents: 1499,
        worthCents: 3991,
    },
    // A lone mount, in no pack: sold on the shop's home.
    {
        id: 'pegasus',
        name: 'Pégase',
        blurb: 'Un cheval ailé d’un blanc de neige, la crinière au vent, qui te porte trois fois et demie plus vite que tes jambes.',
        items: [{ templateId: 'pegasus' }],
        priceCents: 1999,
    },
    // Footstep trails (ADR 0073), in no pack: sold on the shop's home.
    {
        id: 'trail-embers',
        name: 'Traînée de braises',
        blurb: 'Chaque pas soulève des braises qui montent en tourbillonnant avant de s’éteindre.',
        items: [{ templateId: 'trail-embers' }],
        priceCents: 399,
    },
    {
        id: 'trail-frost',
        name: 'Traînée de givre',
        blurb: 'Le sol gèle sous tes pas : des éclats de givre scintillent un instant derrière toi.',
        items: [{ templateId: 'trail-frost' }],
        priceCents: 399,
    },
    {
        id: 'trail-petals',
        name: 'Traînée de pétales',
        blurb: 'Des pétales tombent autour de toi à chaque pas et se posent sur ton chemin.',
        items: [{ templateId: 'trail-petals' }],
        priceCents: 399,
    },
    {
        id: 'trail-leaves',
        name: 'Traînée de feuilles',
        blurb: 'Des feuilles d’automne tourbillonnent dans ton sillage, où que tu marches.',
        items: [{ templateId: 'trail-leaves' }],
        priceCents: 399,
    },
    // The dragon set's look: the six pieces of the dungeon set, worn over the
    // real gear (the stats stay the dungeon's to win), and the six together.
    {
        id: 'dragon-sword-look',
        name: 'Épée du dragon',
        blurb: 'Une lame rouge sang à la garde d’écailles, portée par-dessus ton arme.',
        pack: 'dragon',
        items: [{ templateId: 'dragon-sword-look' }],
        priceCents: 399,
    },
    {
        id: 'dragon-helm-look',
        name: 'Heaume du dragon',
        blurb: 'Un heaume d’écailles rouges aux cornes recourbées.',
        pack: 'dragon',
        items: [{ templateId: 'dragon-helm-look' }],
        priceCents: 299,
    },
    {
        id: 'dragon-chest-look',
        name: 'Plastron du dragon',
        blurb: 'Des plaques d’écailles rouges et or sur les épaules et le torse.',
        pack: 'dragon',
        items: [{ templateId: 'dragon-chest-look' }],
        priceCents: 399,
    },
    {
        id: 'dragon-gloves-look',
        name: 'Gantelets du dragon',
        blurb: 'Des griffes d’écailles rouges aux poings.',
        pack: 'dragon',
        items: [{ templateId: 'dragon-gloves-look' }],
        priceCents: 199,
    },
    {
        id: 'dragon-boots-look',
        name: 'Bottes du dragon',
        blurb: 'Des bottes d’écailles rouges aux chevilles griffues.',
        pack: 'dragon',
        items: [{ templateId: 'dragon-boots-look' }],
        priceCents: 199,
    },
    {
        id: 'dragon-shield-look',
        name: 'Bouclier du dragon',
        blurb: 'Un écu d’écailles rouges frappé d’une tête de dragon.',
        pack: 'dragon',
        items: [{ templateId: 'dragon-shield-look' }],
        priceCents: 299,
    },
    {
        id: 'dragon-wings',
        name: 'Ailes du dragon',
        blurb: 'Des ailes de cuir cramoisi aux os liserés d’or, qui battent dans ton dos. Animées.',
        pack: 'dragon',
        items: [{ templateId: 'dragon-wings' }],
        priceCents: 599,
    },
    {
        id: 'dragon-pack',
        name: 'Pack du Dragon',
        blurb: 'Les six pièces du set du dragon en apparence (épée, heaume, plastron, gantelets, bottes et bouclier) et ses ailes, par-dessus ton équipement.',
        pack: 'dragon',
        items: [
            { templateId: 'dragon-sword-look' },
            { templateId: 'dragon-helm-look' },
            { templateId: 'dragon-chest-look' },
            { templateId: 'dragon-gloves-look' },
            { templateId: 'dragon-boots-look' },
            { templateId: 'dragon-shield-look' },
            { templateId: 'dragon-wings' },
        ],
        priceCents: 999,
        worthCents: 2193,
    },
];
const PARSED = [...DEFINITIONS, ...COSMETIC_OFFERS].map((raw) => ShopOfferSchema.parse(raw));
/**
 * What a bundle's pieces cost one by one, struck through beside its price:
 * the sum of its pack's singles, so it can never drift from their prices.
 * Undefined when a piece of the bundle is not sold alone.
 */
function worthOf(bundle, offers) {
    let worth = 0;
    for (const { templateId } of bundle.items) {
        const single = offers.find((offer) => offer.pack === bundle.pack &&
            offer.items.length === 1 &&
            offer.items[0]?.templateId === templateId);
        if (single === undefined)
            return undefined;
        worth += single.priceCents;
    }
    return worth;
}
/** Parsed once at module load: a malformed offer fails the server at boot. */
export const SHOP_OFFERS = PARSED.map((offer) => {
    if (offer.items.length < 2)
        return offer;
    const worthCents = worthOf(offer, PARSED) ?? offer.worthCents;
    return worthCents === undefined ? offer : { ...offer, worthCents };
});
export function getOffer(id) {
    return SHOP_OFFERS.find((offer) => offer.id === id);
}
/** « 4,99 € »: the price as the shop prints it, the French way. */
export function formatPrice(cents, currency = SHOP_CURRENCY) {
    return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: currency.toUpperCase(),
    }).format(cents / 100);
}
//# sourceMappingURL=shop.js.map